import type { HmacService } from '../services/hmac.service';
import type { SpaIngestBatch } from './metrics.types';
import { randomUnitInterval } from './secure-random.util';

/**
 * Result of a flush attempt. The processor uses this to decide whether to
 * re-buffer events on transient failure. Permanent failures (4xx) are dropped
 * with a warning so a single bad batch can't poison the buffer.
 */
export type IngestFlushOutcome =
  | { kind: 'ok' }
  | { kind: 'transient'; status: number | 'network'; attemptsExhausted: boolean }
  | { kind: 'permanent'; status: number };

export interface BatchedIngestClientOptions {
  /** Frontend-relative URL to POST to (`environment.metrics.ingestUrl`). */
  ingestUrl: string;
  /** Post-rewrite path the backend signs against (`environment.metrics.hmacPath`). */
  hmacPath: string;
  /** Pre-shared HMAC secret matching admin-service `HMAC_SECRET`. */
  hmacSecret: string;
  /** Service used to compute the signature; injected so this class is unit-testable. */
  hmacService: HmacService;
  /**
   * Maximum number of retry attempts for transient (5xx / network) failures.
   * Default 2 (so up to 3 total POSTs per batch).
   */
  maxRetries?: number;
  /** Base backoff delay in ms; doubles per attempt with light jitter. */
  baseBackoffMs?: number;
  /**
   * `fetch` implementation override (test seam). When omitted, the global
   * `fetch` is used.
   */
  fetchImpl?: typeof fetch;
}

/**
 * Posts batches to the SPA ingest endpoint with HMAC-SHA256 signing.
 *
 * Wire contract (must stay byte-identical to `HmacAuthGuard` in common-library):
 *
 *   message   = `${method}:${path}:${JSON.stringify(body)}:${timestamp}`
 *               (when body has any keys; otherwise the `:body` part is omitted)
 *   signature = HMAC-SHA256(message, secret), hex
 *
 * The same body string is used for both signing AND the request body. We
 * deliberately stringify once and pass the string to `fetch` so the bytes the
 * backend's body-parser sees are identical to what we signed. (When Angular
 * `HttpClient` re-stringifies, key order is theoretically the same in V8 but
 * not contractually guaranteed across stacks. This avoids that risk.)
 *
 * `keepalive: true` is set so a final flush triggered from `visibilitychange`
 * or `beforeunload` still completes after the page is gone.
 */
export class BatchedIngestClient {
  private readonly options: Required<Omit<BatchedIngestClientOptions, 'fetchImpl'>> & { fetchImpl?: typeof fetch };

  constructor(options: BatchedIngestClientOptions) {
    this.options = {
      maxRetries: 2,
      baseBackoffMs: 250,
      ...options,
    };
  }

  /**
   * Posts a single batch. Resolves with the outcome. Never throws; the
   * processor must be able to call this from `beforeunload` without losing
   * non-failed events.
   */
  public async flush(batch: SpaIngestBatch): Promise<IngestFlushOutcome> {
    const rawBody = JSON.stringify(batch);
    const timestamp = Date.now().toString();
    const hasBody = batch !== null && batch !== undefined && Object.keys(batch).length > 0;
    const payload = hasBody ? `${this.options.hmacPath}:${rawBody}` : `${this.options.hmacPath}`;
    const message = `POST:${payload}:${timestamp}`;
    const signature = this.options.hmacService.generateHmacSignature(message, this.options.hmacSecret);

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-Signature': signature,
      'X-Timestamp': timestamp,
    };

    return this.postWithRetry(rawBody, headers);
  }

  private async postWithRetry(rawBody: string, headers: Record<string, string>): Promise<IngestFlushOutcome> {
    const fetchFn = this.options.fetchImpl ?? globalThis.fetch.bind(globalThis);
    let lastTransientStatus: number | 'network' = 'network';

    for (let attempt = 0; attempt <= this.options.maxRetries; attempt++) {
      try {
        const response = await fetchFn(this.options.ingestUrl, {
          method: 'POST',
          headers,
          body: rawBody,
          keepalive: true,
          credentials: 'omit',
          mode: 'cors',
        });

        if (response.status === 204 || (response.status >= 200 && response.status < 300)) {
          return { kind: 'ok' };
        }
        if (response.status >= 400 && response.status < 500) {
          // Permanent: malformed payload, bad HMAC, etc. Drop the batch.
          return { kind: 'permanent', status: response.status };
        }
        // 5xx / unexpected: transient.
        lastTransientStatus = response.status;
      } catch {
        // Network-level failure (CORS, DNS, offline, abort). Transient.
        lastTransientStatus = 'network';
      }

      if (attempt < this.options.maxRetries) {
        await this.sleep(this.backoffMs(attempt));
      }
    }

    return {
      kind: 'transient',
      status: lastTransientStatus,
      attemptsExhausted: true,
    };
  }

  private backoffMs(attempt: number): number {
    const base = this.options.baseBackoffMs * Math.pow(2, attempt);
    const jitter = randomUnitInterval() * this.options.baseBackoffMs;
    return base + jitter;
  }

  private sleep(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}
